/**
 * useUnifiedRateAssignments - Hook for managing unified rate assignments view
 *
 * Features:
 * - Fetches all rate assignments across entity types
 * - Supports filtering by entity type, rate, status, search
 * - Provides bulk operations (update dates, reassign, delete)
 * - Pagination support
 */

import { useState, useCallback, useEffect } from 'react';
import { authenticatedFetch } from '@/lib/api';
import { toast } from 'react-toastify';
import type {
  UnifiedRateAssignment,
  UnifiedAssignmentFilters,
  UnifiedAssignmentListResponse,
  BulkOperationResponse,
} from '@/types/rates';

interface UseUnifiedRateAssignmentsOptions {
  autoFetch?: boolean;
  initialFilters?: UnifiedAssignmentFilters;
}

interface UseUnifiedRateAssignmentsReturn {
  // Data
  assignments: UnifiedRateAssignment[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;

  // State
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;

  // Actions
  fetchAssignments: (filters?: UnifiedAssignmentFilters) => Promise<void>;
  refresh: () => Promise<void>;
  setPage: (page: number) => void;
  setFilters: (filters: UnifiedAssignmentFilters) => void;

  // Bulk operations
  bulkUpdateDates: (
    assignmentIds: string[],
    effectiveFrom?: string,
    effectiveTo?: string | null
  ) => Promise<boolean>;
  bulkReassign: (assignmentIds: string[], newRateId: string) => Promise<boolean>;
  bulkDelete: (assignmentIds: string[]) => Promise<boolean>;
}

export function useUnifiedRateAssignments(
  options: UseUnifiedRateAssignmentsOptions = {}
): UseUnifiedRateAssignmentsReturn {
  const { autoFetch = true, initialFilters = {} } = options;

  const [assignments, setAssignments] = useState<UnifiedRateAssignment[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialFilters.page || 1);
  const [pageSize] = useState(initialFilters.pageSize || 50);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFiltersState] = useState<UnifiedAssignmentFilters>(initialFilters);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAssignments = useCallback(
    async (overrideFilters?: UnifiedAssignmentFilters) => {
      setIsLoading(true);
      setError(null);

      try {
        const currentFilters = { ...filters, ...overrideFilters };
        const params = new URLSearchParams();

        if (currentFilters.entityType) params.set('entityType', currentFilters.entityType);
        if (currentFilters.rateId) params.set('rateId', currentFilters.rateId);
        if (currentFilters.status) params.set('status', currentFilters.status);
        if (currentFilters.companyId) params.set('companyId', currentFilters.companyId);
        if (currentFilters.search) params.set('search', currentFilters.search);
        if (currentFilters.sortBy) params.set('sortBy', currentFilters.sortBy);
        if (currentFilters.sortOrder) params.set('sortOrder', currentFilters.sortOrder);

        params.set('page', String(currentFilters.page || page));
        params.set('pageSize', String(currentFilters.pageSize || pageSize));

        const response = await authenticatedFetch(
          `/api/v1/admin/rate-assignments/all?${params.toString()}`
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch assignments');
        }

        const data: UnifiedAssignmentListResponse = await response.json();

        setAssignments(data.data);
        setTotal(data.pagination.total);
        setHasMore(data.pagination.hasMore);
        setPage(data.pagination.page);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch assignments';
        setError(message);
        console.error('[useUnifiedRateAssignments] Fetch error:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [filters, page, pageSize]
  );

  const refresh = useCallback(() => {
    return fetchAssignments();
  }, [fetchAssignments]);

  const setFilters = useCallback((newFilters: UnifiedAssignmentFilters) => {
    setFiltersState(newFilters);
    setPage(1); // Reset to first page when filters change
  }, []);

  const setPageNumber = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // Bulk update dates
  const bulkUpdateDates = useCallback(
    async (
      assignmentIds: string[],
      effectiveFrom?: string,
      effectiveTo?: string | null
    ): Promise<boolean> => {
      if (assignmentIds.length === 0) {
        toast.warning('No assignments selected');
        return false;
      }

      setIsSubmitting(true);

      try {
        const response = await authenticatedFetch('/api/v1/admin/rate-assignments/bulk-update-dates', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assignmentIds, effectiveFrom, effectiveTo }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update dates');
        }

        const result: BulkOperationResponse = await response.json();

        toast.success(`Updated dates for ${result.updated} assignment(s)`);
        await refresh();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update dates';
        toast.error(message);
        console.error('[useUnifiedRateAssignments] Bulk update dates error:', err);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [refresh]
  );

  // Bulk reassign
  const bulkReassign = useCallback(
    async (assignmentIds: string[], newRateId: string): Promise<boolean> => {
      if (assignmentIds.length === 0) {
        toast.warning('No assignments selected');
        return false;
      }

      setIsSubmitting(true);

      try {
        const response = await authenticatedFetch('/api/v1/admin/rate-assignments/bulk-reassign', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assignmentIds, newRateId }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to reassign');
        }

        const result: BulkOperationResponse = await response.json();

        toast.success(
          `Reassigned ${result.reassigned} assignment(s) to "${result.newRateName}"`
        );
        await refresh();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to reassign';
        toast.error(message);
        console.error('[useUnifiedRateAssignments] Bulk reassign error:', err);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [refresh]
  );

  // Bulk delete
  const bulkDelete = useCallback(
    async (assignmentIds: string[]): Promise<boolean> => {
      if (assignmentIds.length === 0) {
        toast.warning('No assignments selected');
        return false;
      }

      setIsSubmitting(true);

      try {
        const response = await authenticatedFetch('/api/v1/admin/rate-assignments/bulk-delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assignmentIds }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete');
        }

        const result: BulkOperationResponse = await response.json();

        toast.success(`Deleted ${result.deleted} assignment(s)`);
        await refresh();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete';
        toast.error(message);
        console.error('[useUnifiedRateAssignments] Bulk delete error:', err);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [refresh]
  );

  // Auto-fetch on mount and filter changes
  useEffect(() => {
    if (autoFetch) {
      fetchAssignments();
    }
  }, [filters, page]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    assignments,
    total,
    page,
    pageSize,
    hasMore,
    isLoading,
    isSubmitting,
    error,
    fetchAssignments,
    refresh,
    setPage: setPageNumber,
    setFilters,
    bulkUpdateDates,
    bulkReassign,
    bulkDelete,
  };
}
